/**
 * React context that provides the single API trio (api client, TanStack query
 * client, oRPC query utils) to the component tree, and mounts TanStack's
 * `QueryClientProvider` so `useQueryClient()` works underneath it.
 *
 * Production creates the trio once at the app root; test and demo wrappers
 * create their own so each rendered tree gets an isolated client. React
 * components read the api client with `useApiClient()`, the query utils with
 * `useApiQueryUtils()`, and the query client with TanStack's
 * `useQueryClient()`. Non-hook code (callbacks, non-React helpers) reaches the
 * same instances via `datastore.getApiClient()` / `getQueryClient()` /
 * `getApiQueryUtils()`, which are pure passthroughs to what the provider
 * handed the Datastore.
 */

import type { RouterUtils } from '@orpc/react-query';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { createContext, useContext } from 'react';
import type { ApiClient } from 'system/orpc';

type ApiClientContextValue = {
    apiClient: ApiClient;
    queryClient: QueryClient;
    apiQueryUtils: RouterUtils<ApiClient>;
};

const ApiClientContext = createContext<ApiClientContextValue | null>(null);

export function ApiClientProvider({
    apiClient,
    queryClient,
    apiQueryUtils,
    children,
}: ApiClientContextValue & { children: React.ReactNode }) {
    const value = React.useMemo(() => ({ apiClient, queryClient, apiQueryUtils }), [apiClient, queryClient, apiQueryUtils]);
    return (
        <ApiClientContext.Provider value={value}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </ApiClientContext.Provider>
    );
}

function useApiClientContext(): ApiClientContextValue {
    const ctx = useContext(ApiClientContext);
    if (!ctx) {
        throw new Error('useApiClient/useApiQueryUtils must be used within an <ApiClientProvider>');
    }
    return ctx;
}

export function useApiClient(): ApiClient {
    return useApiClientContext().apiClient;
}

export function useApiQueryUtils(): RouterUtils<ApiClient> {
    return useApiClientContext().apiQueryUtils;
}
